import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import TicketingContext from '@totem/components/ticketing/listContainer/ticketingContainerContext';
import {
  TicketingFilterOptions,
  TicketQueryResult,
  TicketQueryResults,
} from '@totem/components/ticketing/ticketing';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ObjectReference, Params } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { TICKETS_ENDPOINT } from '@totem/utilities/endpoints';
import { getPriorityFilter, getStatusFilter } from '@totem/utilities/ticketing';

import '../ticketing.css';

export const containerConfiguration: FilterField[] = [
  {
    key: 'id',
    label: '',
    sortField: 'id',
    filterField: '_id',
    multiple: true,
    values: [],
    hideFilterChips: true,
    allowPersistence: false,
  },
  {
    key: 'number',
    label: 'Ticket Number',
    sortField: 'ticket.number',
    filterField: 'number',
    multiple: true,
    values: [],
  },
  {
    key: 'type',
    label: 'Ticket Type',
    sortField: 'ticket.type',
    filterField: 'type',
    multiple: true,
    values: [],
  },
  {
    key: 'category',
    label: 'Category',
    sortField: 'ticket.category',
    filterField: 'category',
    multiple: true,
    values: [],
  },
  {
    key: 'subCategory',
    label: 'Subcategory',
    sortField: 'ticket.subCategory',
    filterField: 'subCategory',
    multiple: true,
    values: [],
  },
  {
    key: 'organization',
    label: 'Organization',
    sortField: 'organization.name',
    filterField: 'organizationId',
    multiple: true,
    values: [],
  },
  {
    key: 'building',
    label: 'Building',
    sortField: 'building.name',
    filterField: 'buildingId',
    multiple: true,
    values: [],
  },
  {
    key: 'controlSystem',
    label: 'Control System',
    sortField: 'controlSystem.name',
    filterField: 'controlSystemId',
    multiple: true,
    values: [],
  },
  {
    key: 'device',
    label: 'Device',
    sortField: 'device.name',
    filterField: 'deviceId',
    multiple: true,
    values: [],
  },
  {
    key: 'summary',
    label: 'Summary',
    sortField: 'ticket.summary',
    filterField: '',
    multiple: true,
    values: [],
  },
  {
    key: 'priority',
    label: 'Priority',
    sortField: 'ticket.priority',
    filterField: 'priority',
    multiple: true,
    values: [],
  },
  {
    key: 'impact',
    label: 'Impact',
    sortField: 'ticket.impact',
    filterField: 'impact',
    multiple: true,
    values: [],
  },
  {
    key: 'urgency',
    label: 'Urgency',
    sortField: 'ticket.urgency',
    filterField: 'urgency',
    multiple: true,
    values: [],
  },
  {
    key: 'status',
    label: 'Status',
    sortField: 'ticket.status',
    filterField: 'status',
    multiple: true,
    values: [],
  },
  {
    key: 'createdAt',
    label: 'Created Time',
    sortField: 'ticket.createdAt',
    filterField: '',
    multiple: true,
    values: [],
  },
  {
    key: 'age',
    label: 'Age',
    sortField: 'ticket.createdAt',
    filterField: '',
    multiple: false,
    values: [],
    hideFilterChips: true,
    invertSort: true,
  },
  {
    key: 'updatedAt',
    label: 'Updated Time',
    sortField: 'ticket.updatedAt',
    filterField: '',
    multiple: true,
    values: [],
  },
  {
    key: 'reference',
    label: 'Reference',
    sortField: '',
    filterField: 'keys',
    multiple: true,
    values: [],
  },
  {
    key: 'queueId',
    label: 'Queue',
    sortField: '',
    filterField: 'queueId',
    multiple: true,
    values: [],
  },
];

type Props = {
  refresh?: boolean;
  onRecordTotalChanged?: (total: number) => void;
  onDataRefreshRequested?: () => void;
  onTicketSelected?: (ticketId: string, ticket: TicketQueryResult) => void;
  children?: ReactNode;
  ticketEndPoint?: string;
  componentKey?: string;
};

const TicketListContainer = ({
  onRecordTotalChanged,
  onDataRefreshRequested,
  refresh,
  children,
  ticketEndPoint,
  onTicketSelected,
}: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);

  // Two separate effects below can each request a refetch in quick succession
  // (e.g. one fires the instant an org switch changes userProfile.organization.id,
  // before filters have been reset for the new org; another fires once the
  // correct filters for that org are actually applied). Fetches aren't
  // cancelled, so responses can resolve out of order — this tracks which
  // request is the latest so a stale response can never overwrite a fresher one.
  const latestRequestId = useRef(0);

  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [isFilterOptionsLoaded, setIsFilterOptionsLoaded] =
    useState<boolean>(false);

  const [ticketData, setTicketData] = useState<TicketQueryResults>(null);
  const [selectedTickets, setSelectedTickets] = useState<ObjectReference[]>([]);

  const {
    buildParameters,
    setFilterOptions,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    isReady,
  } = useContext(InputContext);

  useEffect(() => {
    if (isReady) {
      setRefreshData(true);
    }
  }, [isReady, filterUpdateCount, pagingUpdateCount, sortingUpdateCount]);

  useEffect(() => {
    if (
      typeof onRecordTotalChanged !== 'undefined' &&
      onRecordTotalChanged !== null
    ) {
      onRecordTotalChanged(totalRecords);
    }
  }, [onRecordTotalChanged, totalRecords]);

  useEffect(() => {
    if (
      refresh &&
      typeof onDataRefreshRequested !== 'undefined' &&
      onDataRefreshRequested !== null
    ) {
      setRefreshData(true);

      onDataRefreshRequested();
    }
  }, [refresh, onDataRefreshRequested]);

  useEffect(() => {
    setRefreshData(true);
  }, [
    ticketEndPoint,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    userProfile.organization.id,
  ]);

  const handleSelectTicket = (ticketId: string, ticketNumber: string) => {
    const foundIdx = selectedTickets.findIndex((chk) => chk.id === ticketId);
    if (foundIdx === -1) {
      const newReference: ObjectReference = {
        id: ticketId,
        name: ticketNumber,
      };
      setSelectedTickets((prevState) => [...prevState, newReference]);
    }
  };

  const handleDeselectTicket = (ticketId: string) => {
    setSelectedTickets((prevState) =>
      prevState.filter((chk) => chk.id !== ticketId),
    );
  };

  useEffect(() => {
    setIsFilterOptionsLoaded(false);
  }, [userProfile.organization.id]);

  useEffect(() => {
    if (!isFilterOptionsLoaded) {
      fetch(`${TICKETS_ENDPOINT}/filterOptions`, {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      })
        .then((res) => res.json())
        .then((result: TicketingFilterOptions) => {
          setFilterOptions('priority', getPriorityFilter());

          setFilterOptions('status', getStatusFilter());

          if (isNotNull(result)) {
            if (isNotNull(result.type)) {
              setFilterOptions(
                'type',
                result.type.map((opt) => {
                  return { text: opt.name, value: opt.value };
                }),
              );
            }

            if (isNotNull(result.impact)) {
              setFilterOptions(
                'impact',
                result.impact.map((opt) => {
                  return { text: opt.name, value: opt.value };
                }),
              );
            }

            if (isNotNull(result.urgency)) {
              setFilterOptions(
                'urgency',
                result.urgency.map((opt) => {
                  return { text: opt.name, value: opt.value };
                }),
              );
            }

            if (isNotNull(result.subStatus)) {
              setFilterOptions(
                'subStatus',
                result.subStatus.map((opt) => {
                  return { text: opt.name, value: opt.value };
                }),
              );
            }

            if (isNotNull(result.categories)) {
              setFilterOptions(
                'category',
                result.categories.map((opt) => {
                  return { text: opt.name, value: opt.value };
                }),
              );
            }

            if (isNotNull(result.building)) {
              setFilterOptions(
                'building',
                result.building.map((opt) => {
                  return { text: opt.name, value: opt.value };
                }),
              );
            }

            if (isNotNull(result.organization)) {
              setFilterOptions(
                'organization',
                result.organization.map((opt) => {
                  return { text: opt.name, value: opt.value };
                }),
              );
            }

            if (isNotNull(result.device)) {
              setFilterOptions(
                'device',
                result.device.map((opt) => {
                  return { text: opt.name, value: opt.value };
                }),
              );
            }
          }
        })
        .then(() => {
          setIsFilterOptionsLoaded(true);
        });
    }
  }, [isFilterOptionsLoaded, getApiToken]);

  useEffect(() => {
    if (!isReady) return;
    if (refreshData) {
      setIsLoading(true);
      const requestId = ++latestRequestId.current;
      const params: Params = buildParameters();
      const ticketUrl =
        typeof ticketEndPoint !== 'undefined' && ticketEndPoint !== null
          ? ticketEndPoint
          : TICKETS_ENDPOINT;
      fetch(`${ticketUrl}`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(params),
      })
        .then((res) => res.json())
        .then((result: TicketQueryResults) => {
          if (latestRequestId.current !== requestId) return;

          setTicketData(result);

          setTotalRecords(result.paging.totalRecords);
        })
        .then(() => {
          if (latestRequestId.current === requestId) {
            setIsLoading(false);
          }
        });

      setRefreshData(false);
    }
  }, [refreshData, refresh, getApiToken, isReady]);

  return (
    <TicketingContext.Provider
      value={{
        loading: isLoading,
        ticketData,
        totalRecords,
        onTicketSelected,
        selectedTickets,
        clearSelectedTickets: () => setSelectedTickets([]),
        selectTicket: handleSelectTicket,
        deselectTicket: handleDeselectTicket,
        refetch: () => {
          setRefreshData(true);
        },
      }}
    >
      <div>{children}</div>
    </TicketingContext.Provider>
  );
};

export default TicketListContainer;
